/** @format */

import React, { useEffect, useRef, useState } from "react";
import { isDesktop, isMobileOnly, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import {
  Box,
  IdeaCardHeader,
  IdeaDetailTemplate,
  MainTemplatesLayout,
  Report,
  Truncater,
  Typography,
  useDiscardModal,
  useIsMobile,
} from "senf-atomic-design-system";
import Button from "senf-atomic-design-system/src/components/core/button/Button";
import { useModals } from "senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import { useAuthContext } from "senf-shared";
import { deleteIdea, getIdea } from "../redux/actions/ideaActions";
import { DELETE_COMMENT, SET_IDEA, SUBMIT_COMMENT } from "../redux/types";

const IdeaDetailPage = ({ ideaMap, handle: projectHandle, user }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { openModal, closeModal } = useModals();
  const { deleteComment, submitComment } = useAuthContext();
  const data = useSelector((state) => state.data.idea);
  const project = useSelector((state) => state.data.project);
  const [searchParams, setSearchParams] = useSearchParams();
  const idea = useSelector((state) => state.data.idea);
  const { displayDiscardModal } = useDiscardModal();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { ideaMapId, ideaId } = useParams();
  const { pathname } = useLocation();
  useEffect(() => {
    if (ideaId && project) {
      dispatch(getIdea(ideaId, project, ideaMap));
    }
  }, [ideaId, ideaMap, project]);

  const [commentFormInput, setCommentFormInput] = useState("");
  const [commentFormLoading, setCommentFormLoading] = useState(false);
  useEffect(() => {
    if (ideaId && idea?.lat !== undefined) {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "smooth",
      });
    }
  }, [idea?.lat, ideaId]);

  const handle = {
    buttonLike: projectHandle.buttonLike,
    buttonComment: projectHandle.buttonComment,

    closeCard: () => {
      dispatch({ type: SET_IDEA, payload: null });
      const rootProfileId = searchParams.get("rootProfileId");

      if (rootProfileId) {
        navigate(
          `${pathname
            .split("/")
            .slice(0, -1)
            .join("/")}?profile=${rootProfileId}`,
        );
      } else {
        navigate(`${pathname.split("/").slice(0, -1).join("/")}`);
      }
    },

    submitComment: async () => {
      setCommentFormLoading(true);
      const newComment = await submitComment(
        project,
        ideaMapId,
        ideaId,
        { body: commentFormInput },
        user,
        isMobileOnly
          ? "mobile"
          : isTablet
            ? "tablet"
            : isDesktop
              ? "desktop"
              : "other",
      );

      dispatch({
        type: SUBMIT_COMMENT,
        payload: newComment,
      });
      setCommentFormInput("");
      setCommentFormLoading(false);
    },

    deleteIdea: (ideaId) => {
      dispatch(deleteIdea(project, ideaId, user)).then(() => {
        navigate(`${pathname.split("/").slice(0, -1).join("/")}`);
      });
    },

    deleteComment: async ({ commentId }) => {
      closeModal();
      const deletedCommentId = await deleteComment(
        project,
        commentId,
        user,
        ideaId,
      );
      dispatch({
        type: DELETE_COMMENT,
        payload: deletedCommentId,
      });
    },

    reportIdea: (data) => {
      openModal(<Report data={data} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
    },
    reportComment: (data) => {
      openModal(<Report data={data} project={project} />, {
        swipe: !!isMobile,
        size: isMobile && "fullscreen",
      });
    },
    openProfilePage: (profileId) => {
      navigate(`?profile=${profileId}`);
    },
  };

  const catchRef = useRef();
  const [scrollBumped, setScrollBumped] = useState(false);

  useEffect(() => {
    const observerCallback = ([entry]) => {
      const sticky = entry.intersectionRatio < 1;
      setScrollBumped(sticky);
      entry.target.classList.toggle("isSticky", sticky);
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: [1],
    });

    if (catchRef.current) observer.observe(catchRef.current);

    // Clean up the observer on component unmount
    return () => {
      if (catchRef.current) observer.unobserve(catchRef.current);
    };
  }, []);

  const initialFocusRef = useRef(null);
  const lastFocusedElementRef = useRef(null);

  useEffect(() => {
    if (!data?.title) return undefined;
    // Capture the current focus before the component mounts
    lastFocusedElementRef.current = document.activeElement;
    // Focus the close button initially
    initialFocusRef.current?.focus();
    // Restore focus to the previously focused element when the component unmounts
    return () => {
      if (lastFocusedElementRef.current) {
        lastFocusedElementRef.current.focus();
      }
    };
  }, [data?.title]);

  return (
    <MainTemplatesLayout
      handleButtonClose={() => handle.closeCard()}
      initialFocusRef={initialFocusRef}
      withSpecialHeader={true}
      shouldSnapToTop={projectHandle.shouldSnapToTop}
      handleButtonEditData={
        user?.userId === idea?.userId ||
        user?.isSuperAdmin === true ||
        user?.isAdmin === true
          ? [
              {
                text: t("util:edit", { val: t("global:post") }),
                leadingIcon: "edit",
                onClick: () => navigate("editIdea"),
              },
              {
                text: t("util:delete", { val: t("global:post") }),
                leadingIcon: "trash",
                onClick: () =>
                  displayDiscardModal({
                    title: t("util:delete", { val: t("global:post") }),
                    description: t("global:deleteDescription"),
                    submitText: t("global:delete"),
                    onSubmit: () => handle.deleteIdea(ideaId),
                  }),
              },
            ]
          : [
              {
                text: t("util:report", { val: t("global:post") }),
                onClick: () => handle.reportIdea(data),
                leadingIcon: "alert",
              },
            ]
      }
      header={
        <header className="mb-3.5 mt-2 flex items-center gap-2.5">
          {scrollBumped ? (
            <Typography variant="buttonBg" color="black">
              <Truncater
                lines={1}
                maxWidth={isMobile ? `${window.innerWidth - 120}px` : "290px"}
              >
                {data?.title}
              </Truncater>
            </Typography>
          ) : (
            <IdeaCardHeader
              data={data}
              maxWidth={isMobile ? `${window.innerWidth - 150}px` : "260px"}
            />
          )}
        </header>
      }
      options={{
        open: true,
        openTo: "middle",
        positions: {
          top: window.innerHeight - 10,
          middle: window.innerHeight / 2,
          bottom: 180,
        },
      }}
      closeButtonTooltipLabel={t("util:close", { val: t("global:post") })}
    >
      <IdeaDetailTemplate
        ideaMap={ideaMap}
        data={{ ...data, handle }}
        user={user}
        commentFormInput={commentFormInput}
        setCommentFormInput={setCommentFormInput}
        commentFormLoading={commentFormLoading}
        catchRef={catchRef}
        project={project}
      />
    </MainTemplatesLayout>
  );
};

export default IdeaDetailPage;
